/**
 * Per-wallet device credentials.  Private keys deliberately stay in IndexedDB
 * as non-extractable CryptoKeys; only the public JWKs are ever serialised.
 */
import { openDB } from 'idb';
const DATABASE = 'dwdata-chat-device-identities';
const STORE = 'identities';
const IDENTITY_VERSION = 1;
export function normalizeChatWallet(wallet) {
    const normalized = wallet.trim().toLowerCase();
    if (!normalized)
        throw new Error('A wallet identifier is required');
    return normalized;
}
function randomDeviceId() {
    const bytes = crypto.getRandomValues(new Uint8Array(18));
    // base64url, without padding, is URL/header safe and does not identify a wallet.
    let binary = '';
    bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
async function database() {
    if (typeof indexedDB === 'undefined') {
        throw new Error('IndexedDB is unavailable; secure device identity cannot be persisted');
    }
    return openDB(DATABASE, 1, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE))
                db.createObjectStore(STORE);
        },
    });
}
function isStoredIdentity(value, wallet) {
    const item = value;
    return Boolean(item &&
        item.version === IDENTITY_VERSION &&
        item.wallet === wallet &&
        typeof item.deviceId === 'string' &&
        item.signingPrivateKey &&
        item.ecdhPrivateKey &&
        item.signingPublicJwk &&
        item.ecdhPublicJwk);
}
/**
 * Gets the stable identity for a wallet in this browser profile.  Generation
 * fails closed on WebCrypto implementations which cannot persist CryptoKey
 * objects rather than falling back to exportable key bytes.
 */
export async function getOrCreateWalletDeviceIdentity(wallet) {
    const normalizedWallet = normalizeChatWallet(wallet);
    const db = await database();
    const existing = await db.get(STORE, normalizedWallet);
    if (isStoredIdentity(existing, normalizedWallet))
        return existing;
    const signing = (await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']));
    const ecdh = (await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']));
    const identity = {
        version: IDENTITY_VERSION,
        wallet: normalizedWallet,
        deviceId: randomDeviceId(),
        signingPrivateKey: signing.privateKey,
        signingPublicJwk: await crypto.subtle.exportKey('jwk', signing.publicKey),
        ecdhPrivateKey: ecdh.privateKey,
        ecdhPublicJwk: await crypto.subtle.exportKey('jwk', ecdh.publicKey),
    };
    try {
        await db.put(STORE, identity, normalizedWallet);
    }
    catch (cause) {
        throw new Error(`This browser cannot persist non-exportable device keys: ${cause instanceof Error ? cause.message : 'unknown error'}`);
    }
    return identity;
}
/** Deletes only this browser's local identity (for explicit device reset). */
export async function clearWalletDeviceIdentity(wallet) {
    const db = await database();
    await db.delete(STORE, normalizeChatWallet(wallet));
}
export async function signDeviceChallenge(identity, challenge) {
    return new Uint8Array(await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, identity.signingPrivateKey, challenge));
}
